import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/KebunLP.css";

const KebunLP = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tanggal, setTanggal] = useState("");
  const [userInfo, setUserInfo] = useState({
    role: "Guest",
    name: "Guest",
    full_name: "Guest",
  });

  const [permohonanData, setPermohonanData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    setTanggal(
      new Date().toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    );

    axios
      .get("https://c-greenproject.org:7500/get_user_role", { withCredentials: true })
      .then((res) => {
        if (!res.data.error) {
          setUserInfo({
            role: res.data.role,
            name: res.data.name,
            full_name: res.data.full_name,
          });
        }
      })
      .catch((err) => console.error(err));

    const fetchPermohonanData = async () => {
      try {
        const response = await axios.get(
          "https://c-greenproject.org:7500/permohonan_belum_divalidasi",
          { withCredentials: true }
        );
        setPermohonanData(response.data.permohonan_belum_divalidasi || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermohonanData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(
        "https://c-greenproject.org:7500/logout",
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.warn("Logout request fallback:", error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      navigate("/");
    }
  };

  const handleValidate = (pengiriman_id) => {
    Swal.fire({
      title: "Validasi Pengiriman",
      text: "Setujui atau tolak permintaan penjemputan logistik ini?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "✓ Setujui",
      cancelButtonText: "✕ Tolak",
      customClass: {
        popup: "rounded-4",
        confirmButton: "rounded-pill px-4",
        cancelButton: "rounded-pill px-4",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        submitValidation(pengiriman_id, "approved", "");
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire({
          title: "Alasan Penolakan",
          input: "textarea",
          inputPlaceholder: "Tuliskan alasan penolakan...",
          inputValidator: (val) => (!val ? "Alasan wajib diisi!" : null),
          showCancelButton: true,
          confirmButtonText: "Kirim Penolakan",
          confirmButtonColor: "#ef4444",
          customClass: { popup: "rounded-4", confirmButton: "rounded-pill px-4" },
        }).then((noteRes) => {
          if (noteRes.isConfirmed) {
            submitValidation(pengiriman_id, "rejected", noteRes.value);
          }
        });
      }
    });
  };

  const submitValidation = (pengiriman_id, status, note) => {
    axios
      .post(
        "https://c-greenproject.org:7500/validate_pengiriman",
        {
          pengiriman_id,
          status,
          note,
        },
        { withCredentials: true }
      )
      .then((res) => {
        Swal.fire({
          title: "Berhasil!",
          text: res.data.message || "Status validasi berhasil diperbarui",
          icon: "success",
          confirmButtonColor: "#10b981",
          customClass: { popup: "rounded-4", confirmButton: "rounded-pill px-4" },
        });

        setPermohonanData((prev) =>
          prev.map((item) =>
            item.pengiriman_id === pengiriman_id
              ? { ...item, status_permohonan: status }
              : item
          )
        );
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Terjadi kesalahan proses validasi.";
        Swal.fire("Error", msg, "error");
      });
  };

  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase().trim();
    if (s === "approved" || s === "disetujui") {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-3 py-1 small fw-semibold">
          Approved
        </span>
      );
    }
    if (s === "rejected" || s === "ditolak") {
      return (
        <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-3 py-1 small fw-semibold">
          Rejected
        </span>
      );
    }
    return (
      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill px-3 py-1 small fw-semibold">
        Pending
      </span>
    );
  };

  const getStatusLogistikBadge = (statusLogistik) => {
    const s = String(statusLogistik || "").toLowerCase().trim();
    if (s === "approved" || s === "disetujui") {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2.5 py-1 small fw-semibold">
          Approved
        </span>
      );
    }
    if (s === "rejected" || s === "ditolak") {
      return (
        <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-2.5 py-1 small fw-semibold">
          Rejected
        </span>
      );
    }
    return (
      <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill px-2.5 py-1 small fw-semibold">
        Pending
      </span>
    );
  };

  const filteredData = useMemo(() => {
    return permohonanData
      .filter((item) => {
        const s = String(item.status_permohonan || "pending").toLowerCase();
        if (statusFilter !== "all" && s !== statusFilter) return false;

        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        const idStr = String(item.pengiriman_id);
        const panenIdStr = String(item.rencana_panen_id);
        const trackStr = String(item.tracking_id || "").toLowerCase();
        const logistikName = String(item.nama_logistik || "").toLowerCase();
        const logistikContact = String(item.kontak_logistik || "").toLowerCase();

        return (
          idStr.includes(term) ||
          panenIdStr.includes(term) ||
          trackStr.includes(term) ||
          logistikName.includes(term) ||
          logistikContact.includes(term)
        );
      })
      .sort((a, b) => b.pengiriman_id - a.pengiriman_id);
  }, [permohonanData, statusFilter, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: "#f8fafc" }}>
      <style>
        {`
          .dataTables_paginate,
          .dataTables_info,
          .dataTables_length,
          .dataTables_filter {
            display: none !important;
          }
        `}
      </style>

      <div className="d-flex justify-content-between align-items-center px-4 py-2.5 bg-white border-bottom shadow-sm">
        <img src="/static/logo_potrait.png" alt="Logo" style={{ height: "36px" }} />
        <div className="d-flex align-items-center gap-3">
          <span className="fw-semibold text-secondary small">{userInfo.full_name} |</span>
          <button className="btn btn-outline-danger btn-sm rounded-pill px-3 py-1" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </div>

      <div className="d-flex flex-grow-1">
        <div className="p-3 bg-white border-end shadow-sm" style={{ width: "240px", flexShrink: 0 }}>
          <p className="text-muted small mb-1">Portal Traceability</p>
          <h6 className="fw-bold mb-2 text-dark">Telsa Super Apps</h6>
          <p className="text-muted small d-flex align-items-center gap-2 mb-4" style={{ fontSize: "12px" }}>
            <img src="/static/schedule_11145854.png" alt="Date" width="14" />
            {tanggal}
          </p>

          <div className="d-flex flex-column gap-1">
            <div
              onClick={() => navigate("/kebun")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname === "/kebun" ? "bg-success text-white fw-bold" : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/eco-home_7633847.png" alt="Home" width="18" /> Beranda
            </div>
            <div
              onClick={() => navigate("/KebunP")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname.includes("kebunp") && !location.pathname.includes("kebunpabrik")
                  ? "bg-success text-white fw-bold"
                  : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/Tanam.png" alt="Planting" width="18" /> Petani
            </div>
            <div
              onClick={() => navigate("/KebunL")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname.toLowerCase().includes("kebunl")
                  ? "bg-success text-white fw-bold"
                  : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/shipping.png" alt="Logistics" width="18" /> Logistik
            </div>
            <div
              onClick={() => navigate("/kebunpabrik")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname === "/kebunpabrik" ? "bg-success text-white fw-bold" : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/factory.png" alt="Factory" width="18" /> Pabrik
            </div>
          </div>
        </div>

        <div className="flex-grow-1 p-4" style={{ width: "calc(100% - 240px)", overflowX: "hidden" }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <button
                  onClick={() => navigate("/KebunL")}
                  className="btn btn-sm btn-link text-decoration-none p-0 text-muted small"
                >
                  Logistik
                </button>
                <span className="text-muted small">/</span>
                <span className="text-dark small fw-semibold">Pengajuan Validasi</span>
              </div>
              <h3 className="fw-bold text-dark mb-0">Daftar Pengajuan Logistik</h3>
            </div>

            <button
              className="btn btn-light bg-white border rounded-pill px-3 py-1.5 small fw-medium shadow-sm"
              onClick={() => navigate("/KebunL")}
            >
              ← Kembali
            </button>
          </div>

          <div className="bg-white rounded-4 border shadow-sm p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 pb-3 mb-3 border-bottom">
              <div className="btn-group btn-group-sm p-1 bg-light rounded-pill border">
                <button
                  type="button"
                  className={`btn rounded-pill px-3 py-1 border-0 ${
                    statusFilter === "all" ? "btn-success text-white fw-semibold shadow-sm" : "btn-light text-secondary"
                  }`}
                  style={{ fontSize: "12px" }}
                  onClick={() => { setStatusFilter("all"); setCurrentPage(1); }}
                >
                  Semua ({permohonanData.length})
                </button>
                <button
                  type="button"
                  className={`btn rounded-pill px-3 py-1 border-0 ${
                    statusFilter === "pending" ? "btn-success text-white fw-semibold shadow-sm" : "btn-light text-secondary"
                  }`}
                  style={{ fontSize: "12px" }}
                  onClick={() => { setStatusFilter("pending"); setCurrentPage(1); }}
                >
                  Pending ({permohonanData.filter((d) => String(d.status_permohonan || "pending").toLowerCase() === "pending").length})
                </button>
                <button
                  type="button"
                  className={`btn rounded-pill px-3 py-1 border-0 ${
                    statusFilter === "approved" ? "btn-success text-white fw-semibold shadow-sm" : "btn-light text-secondary"
                  }`}
                  style={{ fontSize: "12px" }}
                  onClick={() => { setStatusFilter("approved"); setCurrentPage(1); }}
                >
                  Disetujui ({permohonanData.filter((d) => String(d.status_permohonan).toLowerCase() === "approved").length})
                </button>
                <button
                  type="button"
                  className={`btn rounded-pill px-3 py-1 border-0 ${
                    statusFilter === "rejected" ? "btn-success text-white fw-semibold shadow-sm" : "btn-light text-secondary"
                  }`}
                  style={{ fontSize: "12px" }}
                  onClick={() => { setStatusFilter("rejected"); setCurrentPage(1); }}
                >
                  Ditolak ({permohonanData.filter((d) => String(d.status_permohonan).toLowerCase() === "rejected").length})
                </button>
              </div>

              <div style={{ width: "280px" }}>
                <input
                  type="text"
                  className="form-control form-control-sm rounded-pill px-3 bg-light border"
                  placeholder="Cari ID / Vendor / Tracking..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ fontSize: "12px" }}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success spinner-border-sm me-2" role="status"></div>
                <span className="text-muted small">Memuat data pengajuan...</span>
              </div>
            ) : currentItems.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr className="text-secondary small fw-semibold">
                      <th className="py-2.5 px-3" style={{ width: "100px" }}>ID</th>
                      <th className="py-2.5 px-3">TRACKING & TANGGAL</th>
                      <th className="py-2.5 px-3">VENDOR LOGISTIK TUJUAN</th>
                      <th className="py-2.5 px-3" style={{ width: "130px" }}>REF PANEN</th>
                      <th className="py-2.5 px-3 text-center" style={{ width: "140px" }}>STATUS LOGISTIK</th>
                      <th className="py-2.5 px-3 text-center" style={{ width: "140px" }}>STATUS VALIDASI</th>
                      <th className="py-2.5 px-3 text-end" style={{ width: "110px" }}>AKSI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item) => {
                      const status = String(item.status_permohonan || "pending").toLowerCase();
                      const isPending = status === "pending" || status === "";

                      return (
                        <tr key={`pengiriman-${item.pengiriman_id}`} className="border-bottom">
                          <td className="py-3 px-3">
                            <span className="badge bg-light text-dark border font-monospace px-2.5 py-1.5 rounded-3 fw-bold">
                              #{item.pengiriman_id}
                            </span>
                          </td>

                          <td className="py-3 px-3">
                            {item.tracking_id ? (
                              <div>
                                <span className="fw-semibold text-dark font-monospace d-block" style={{ fontSize: "13px" }}>
                                  🚚 {item.tracking_id}
                                </span>
                                <small className="text-muted" style={{ fontSize: "11px" }}>
                                  {item.tanggal_pengiriman ? `Tgl: ${item.tanggal_pengiriman}` : "Tanggal belum diatur"}
                                </small>
                              </div>
                            ) : (
                              <span className="badge bg-light text-muted fw-normal">Belum Dibuat</span>
                            )}
                          </td>

                          <td className="py-3 px-3">
                            <div>
                              <strong className="text-dark d-block text-capitalize" style={{ fontSize: "13px" }}>
                                {item.nama_logistik || "Belum Ditentukan"}
                              </strong>
                              {item.kontak_logistik && item.kontak_logistik !== "-" && (
                                <small className="text-secondary font-monospace" style={{ fontSize: "11px" }}>
                                  📞 {item.kontak_logistik}
                                </small>
                              )}
                            </div>
                          </td>

                          <td className="py-3 px-3">
                            <span className="badge bg-light text-secondary border font-monospace px-2 py-1">
                              Panen #{item.rencana_panen_id}
                            </span>
                          </td>

                          <td className="py-3 px-3 text-center">
                            {getStatusLogistikBadge(item.status_logistik)}
                          </td>

                          <td className="py-3 px-3 text-center">
                            {getStatusBadge(item.status_permohonan)}
                          </td>

                          <td className="py-3 px-3 text-end">
                            {isPending ? (
                              <button
                                className="btn btn-sm btn-success rounded-pill px-3 py-1 fw-semibold shadow-sm"
                                style={{ fontSize: "12px" }}
                                onClick={() => handleValidate(item.pengiriman_id)}
                              >
                                Validasi
                              </button>
                            ) : (
                              <span className="text-muted small" style={{ fontSize: "12px" }}>
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <span className="fs-1 d-block mb-2">📋</span>
                <p className="mb-0 small fw-medium">Tidak ada data pengajuan yang sesuai.</p>
              </div>
            )}

            {!loading && filteredData.length > 0 && (
              <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 mt-3 border-top gap-2">
                <span className="text-muted small">
                  Menampilkan <b>{(currentPage - 1) * itemsPerPage + 1}</b> -{" "}
                  <b>{Math.min(currentPage * itemsPerPage, filteredData.length)}</b> dari{" "}
                  <b>{filteredData.length}</b> permohonan
                </span>

                <div className="btn-group btn-group-sm">
                  <button
                    className="btn btn-outline-secondary px-2.5"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      className={`btn ${currentPage === p ? "btn-success" : "btn-outline-secondary"} px-3`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    className="btn btn-outline-secondary px-2.5"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KebunLP;